import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { INCIDENT_SEVERITIES, INCIDENT_TYPES, SEVERITY_LABELS, incidentFormSchema, type IncidentSeverity } from "@server/incidents/schema";
import { MAX_FILES } from "@server/uploads/files";
import { ApiError, suggestSeverity, useCreateIncident } from "../api/client";
import { FilePicker, fileRefs, unfinishedFiles, type Attached } from "../components/FilePicker";
import { Button, ErrorBanner, Field, Select } from "../components/ui";
import { Workspace } from "../components/Workspace";

const SEVERITY_OPTIONS = INCIDENT_SEVERITIES.map((severity) => SEVERITY_LABELS[severity]);

interface Values {
  title: string;
  description: string;
  companiesImpacted: string;
  types: string[];
  severity: string;
}

type Errors = Partial<Record<keyof Values, string>>;

const EMPTY: Values = { title: "", description: "", companiesImpacted: "", types: [], severity: "" };

// What the AI said for the text as it was then. `basedOn` lets the form say when the details have changed since.
interface Suggestion {
  status: "loading" | "ready" | "none";
  severity?: IncidentSeverity;
  reason?: string;
  basedOn: string;
}

function validate(values: Values, files: unknown[], aiSeverity?: IncidentSeverity): { errors: Errors; form?: ReturnType<typeof incidentFormSchema.parse> } {
  const severity = INCIDENT_SEVERITIES.find((key) => SEVERITY_LABELS[key] === values.severity);
  const parsed = incidentFormSchema.safeParse({ ...values, severity, aiSeverity, files: files.length > 0 ? files : undefined });
  if (parsed.success) {
    return { errors: {}, form: parsed.data };
  }
  const errors: Errors = {};
  for (const issue of parsed.error.issues) {
    const key = issue.path[0] as keyof Values;
    if (key in EMPTY && !errors[key]) {
      const empty = key !== "types" && values[key] === "";
      errors[key] = empty ? "Required" : issue.message;
    }
  }
  return { errors };
}

export function IncidentForm() {
  const navigate = useNavigate();
  const create = useCreateIncident();
  const [values, setValues] = useState<Values>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const [attached, setAttached] = useState<Attached[]>([]);
  const [filesError, setFilesError] = useState<string>();
  const [suggestion, setSuggestion] = useState<Suggestion>();
  const pending = useRef<AbortController | undefined>(undefined);
  useEffect(() => () => pending.current?.abort(), []);

  const inputKey = JSON.stringify([values.title.trim(), values.description.trim(), values.companiesImpacted.trim(), values.types]);
  const canSuggest = values.title.trim() !== "" && values.description.trim() !== "";
  const askForSeverity = (): void => {
    pending.current?.abort();
    const request = new AbortController();
    pending.current = request;
    const basedOn = inputKey;
    setSuggestion({ status: "loading", basedOn });
    suggestSeverity(
      {
        title: values.title.trim(),
        description: values.description.trim(),
        companiesImpacted: values.companiesImpacted.trim(),
        types: INCIDENT_TYPES.filter((type) => values.types.includes(type)),
      },
      request.signal,
    ).then(
      (result) => setSuggestion(result ? { status: "ready", ...result, basedOn } : { status: "none", basedOn }),
      () => {
        if (!request.signal.aborted) {
          setSuggestion({ status: "none", basedOn });
        }
      },
    );
  };
  const suggested = suggestion?.status === "ready" ? suggestion.severity : undefined;

  const set = <K extends keyof Values>(key: K, value: Values[K]): void => setValues((current) => ({ ...current, [key]: value }));
  const toggleType = (type: string): void => {
    setErrors((current) => ({ ...current, types: undefined }));
    set("types", values.types.includes(type) ? values.types.filter((item) => item !== type) : [...values.types, type]);
  };

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    const { errors: found, form } = validate(values, fileRefs(attached), suggested);
    setErrors(found);
    const unfinished = unfinishedFiles(attached);
    setFilesError(unfinished);
    if (!form || unfinished) {
      return;
    }
    create.mutate(form, { onSuccess: (view) => void navigate(`/incidents/${view.id}`) });
  };

  const sendError = create.error instanceof ApiError ? create.error.message : create.error ? "Something went wrong. Please try again." : undefined;

  return (
    <Workspace step="form" flow="incident">
      <div className="phase-head">
        <h1>Report an incident</h1>
        <p>
          A live incident affecting customers. Submitting creates a dedicated Slack channel, notifies the incident team and gives the incident an owner straight
          away.
        </p>
      </div>

      <form className="issue-form" onSubmit={submit} noValidate>
        <Field id="title" label="Title" error={errors.title} hint="Used to name the dedicated Slack channel for this incident, so keep it short and specific.">
          <input
            id="title"
            type="text"
            maxLength={120}
            autoComplete="off"
            value={values.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder="e.g. Payments failing for EMI companies"
          />
        </Field>

        <Field id="description" label="Description" error={errors.description} hint="Describe the incident in as much detail as possible.">
          <textarea
            id="description"
            maxLength={10_000}
            value={values.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder="What happened, what's affected, and what you know so far"
          />
        </Field>

        <Field
          id="companiesImpacted"
          label="Number of companies impacted (estimate)"
          error={errors.companiesImpacted}
          hint="A number is ideal, but a description is fine if you're not sure yet."
        >
          <input
            id="companiesImpacted"
            type="text"
            maxLength={200}
            autoComplete="off"
            value={values.companiesImpacted}
            onChange={(e) => set("companiesImpacted", e.target.value)}
            placeholder="e.g. 12, or 'all Growth plan companies'"
          />
        </Field>

        <div className="field">
          <span className="field-legend" id="typeLegend">
            Type of incident
          </span>
          <div className={`check-group${errors.types ? " has-error" : ""}`} role="group" aria-labelledby="typeLegend">
            {INCIDENT_TYPES.map((type) => (
              <label key={type} className="check-chip">
                <input type="checkbox" checked={values.types.includes(type)} onChange={() => toggleType(type)} />
                <span className="check-box">✓</span>
                {type}
              </label>
            ))}
          </div>
          <p className="hint">Select all that apply — your best guess is fine. The owner will review and confirm the type while investigating.</p>
          {errors.types && <p className="field-error">{errors.types}</p>}
        </div>

        <Field id="severity" label="Severity" error={errors.severity}>
          <Select id="severity" required value={values.severity} onChange={(e) => set("severity", e.target.value)} options={SEVERITY_OPTIONS} />
        </Field>

        <div className="ai-suggest">
          <div className="ai-suggest-bar">
            <Button type="button" variant="secondary" className="btn-sm" disabled={!canSuggest || suggestion?.status === "loading"} onClick={askForSeverity}>
              {suggestion?.status === "loading" ? "Asking AI…" : suggestion ? "Suggest again" : "Suggest a severity"}
            </Button>
            <span className="hint">
              {canSuggest ? "A suggestion from the title, description and types. You choose the severity." : "Fill in the title and description first."}
            </span>
          </div>
          {suggestion?.status === "ready" && suggested && (
            <div className="ai-suggest-result" role="status">
              <p>
                <strong>AI suggests {SEVERITY_LABELS[suggested]}.</strong> {suggestion.reason}
              </p>
              {suggestion.basedOn !== inputKey && <p className="hint">You have changed the details since. Ask again to update it.</p>}
              {values.severity === SEVERITY_LABELS[suggested] ? (
                <p className="hint">You have chosen the same.</p>
              ) : (
                <Button type="button" variant="secondary" className="btn-sm" onClick={() => set("severity", SEVERITY_LABELS[suggested])}>
                  Use this
                </Button>
              )}
            </div>
          )}
          {suggestion?.status === "none" && <p className="hint">No suggestion this time. Choose the severity yourself.</p>}
        </div>

        <FilePicker
          attached={attached}
          onChange={(update) => setAttached(update)}
          error={filesError}
          hint={`Up to ${MAX_FILES} files, 10 MB each: images, PDF, CSV, XLSX, DOCX or text. They are attached to the incident ticket.`}
        />

        {sendError && <ErrorBanner>{sendError}</ErrorBanner>}
        <div className="form-actions">
          <Button type="submit" disabled={create.isPending || attached.some((item) => item.status === "uploading")}>
            {create.isPending ? "Sending…" : "Create incident →"}
          </Button>
        </div>
      </form>
    </Workspace>
  );
}
